import type { SplitterPaneSize } from "@mantine/hooks";
import type { ReactNode } from "react";

import { Flex, Splitter } from "@mantine/core";
import { useRef, useState } from "react";

import { useAgentStore } from "@/sdk";

import { readWorkspacePanelSizes, writeWorkspacePanelSizes } from "./workspacePanelSize.store";
import { WorkspaceFullWidthTop, WorkspaceLeftMain, WorkspaceRightTop, WorkspaceSidebar } from "./WorkspaceSlots";

/** Resize floors for the one draggable boundary (left-main ↔ right-top). */
const LEFT_MAIN_MIN = "480px";
const RIGHT_TOP_MIN = "440px";

export interface WorkspaceLayoutV2Props {
    /** Optional: when omitted (e.g. voice v2, which has no widget sidebar) no sidebar is rendered. */
    leftSidebar?: ReactNode;
    leftSidebarOpen?: boolean;
    leftSidebarHidden?: boolean;
    leftTop?: ReactNode;
    leftTopOpen: boolean;
    leftTopHeight?: string;
    /**
     * Hoists the left-top bar out of the left pane into one row spanning the whole shell — above the
     * sidebar and both panes. Note the prop names keep saying "leftTop" while this is set, but the bar
     * is then neither left nor inside the splitter region; see the second diagram below.
     */
    leftTopFullWidth?: boolean;
    leftBottom: ReactNode;
    rightTop: ReactNode;
    rightTopOpen: boolean;
    /** Override the right-top default width (e.g. the Expert Hub wants a 30% panel). */
    rightTopSize?: string;
    /** Override the right-top min width; falls back to {@link RIGHT_TOP_MIN} when omitted (on-call). */
    rightTopMin?: string;
    /** Cap the right-top width so a wide viewport doesn't sprawl the panel; uncapped when omitted. */
    rightTopMax?: string;
    /** Persist panel configuration per channel. */
    persistId?: string;
    showExpertHub?: boolean;
}

/**
 * Plain, non-animated workspace shell built on Mantine's `Splitter`. Only the left-main ↔ right-top
 * boundary is user-resizable; the sidebar and left-top call-controls bar are fixed-size bars that
 * simply mount/unmount, so they are plain Boxes rather than resizable panes. This is the shell for
 * the redesigned chat/voice workspaces.
 *
 *     ┌─────────────┬───────────────────────────┬─────────────────────┐
 *     │             │        Left Top (bar)     │                     │
 *     │  Left       ├───────────────────────────┤      Right Top      │
 *     │  Sidebar    │                           │   (resizable vs     │
 *     │  (fixed)    │        Left Bottom        │    left-main)       │
 *     └─────────────┴───────────────────────────┴─────────────────────┘
 *
 * With `leftTopFullWidth` the bar is hoisted into its own row spanning everything, so the left column
 * holds only Left Bottom. The bar is no longer "left" anything, and it sits outside `sidebarAndPanes`
 * rather than within it — the prop name is a leftover from the only place the bar used to live:
 *
 *     ┌───────────────────────────────────────────────────────────────┐
 *     │              Left Top (bar, spans the whole shell)            │
 *     ├─────────────┬───────────────────────────┬─────────────────────┤
 *     │  Left       │                           │      Right Top      │
 *     │  Sidebar    │        Left Bottom        │   (resizable vs     │
 *     │  (fixed)    │                           │    left-main)       │
 *     └─────────────┴───────────────────────────┴─────────────────────┘
 */
export function WorkspaceLayoutV2({
    leftSidebar,
    leftSidebarOpen = false,
    leftSidebarHidden = false,
    leftTop,
    leftTopOpen,
    leftTopHeight = "56px",
    leftTopFullWidth = false,
    leftBottom,
    rightTop,
    rightTopOpen,
    rightTopSize,
    rightTopMin,
    rightTopMax,
    persistId,
    showExpertHub = false,
}: WorkspaceLayoutV2Props) {
    const sidebarVisible = leftSidebarOpen && !leftSidebarHidden;
    const agentId = useAgentStore((state) => state.agentId);
    const leftMainDefaultSize: SplitterPaneSize = !leftSidebarOpen ? "50%" : sidebarVisible ? "45%" : "60%";
    const rightTopDefaultSize = (rightTopSize ?? (leftSidebarOpen ? "40%" : "50%")) as SplitterPaneSize;

    // Mantine's Splitter snapshots its internal size array from the pane count on first mount and never
    // grows it (useSplitter seeds useUncontrolled once). We keep the Splitter mounted across toggles so
    // left-main never remounts, so a right pane added on open would read an undefined size and render at
    // zero width — the panel would look like it never opens. Drive `sizes` as a controlled prop so the
    // array length always matches the rendered pane count.
    const mode = showExpertHub ? "offcall" : "oncall";
    const seedSizes = (open: boolean): SplitterPaneSize[] => {
        if (!open) return [leftMainDefaultSize];
        if (persistId != null) {
            const saved = readWorkspacePanelSizes(agentId, persistId, mode);
            if (saved) return saved;
        }
        return [leftMainDefaultSize, rightTopDefaultSize];
    };
    const [sizes, setSizes] = useState<SplitterPaneSize[]>(() => seedSizes(rightTopOpen));
    const seedKey = `${agentId}:${rightTopOpen}:${mode}`;
    const prevSeedKey = useRef(seedKey);
    if (prevSeedKey.current !== seedKey) {
        prevSeedKey.current = seedKey;
        setSizes(seedSizes(rightTopOpen));
    }

    const leftMain = (
        <WorkspaceLeftMain
            leftTop={leftTopFullWidth ? undefined : leftTop}
            leftTopOpen={leftTopFullWidth ? false : leftTopOpen}
            leftTopHeight={leftTopHeight}
            leftBottom={leftBottom}
        />
    );

    // The sidebar plus the resizable Splitter panes. On the default path this IS the whole shell; with
    // `leftTopFullWidth` it becomes the row below the hoisted bar, which is why the flex sizing below is
    // conditional — as a flex CHILD it must claim the leftover height and be allowed to shrink.
    const sidebarAndPanes = (
        <Flex
            flex={leftTopFullWidth ? "1 1 0" : undefined}
            miw={leftTopFullWidth ? 0 : undefined}
            mih={leftTopFullWidth ? 0 : undefined}
            h="100%"
            w="100%"
        >
            {sidebarVisible && <WorkspaceSidebar>{leftSidebar}</WorkspaceSidebar>}

            {/*
             * Always render the Splitter so left-main keeps a stable parent (Flex > Splitter > Pane[0])
             * across right-top toggles — swapping the container type would remount the whole left-main →
             * ExpertHub subtree, losing its tab/scroll state. `sizes` is controlled (see above) so the
             * pane added on open is sized correctly; closed, the lone pane fills 100% with no handle.
             */}
            <Splitter
                flex="1 1 0"
                miw={0}
                h="100%"
                lineSize={1}
                handleColor="dark.5"
                resetOnDoubleClick={false}
                sizes={sizes}
                onSizeChange={setSizes}
                onResizeEnd={(_handleIndex, next) => {
                    if (persistId != null) {
                        writeWorkspacePanelSizes(agentId, persistId, mode, next);
                    }
                }}
            >
                {/* `defaultSize` is required by the pane type but ignored while `sizes` is controlled. */}
                <Splitter.Pane defaultSize={leftMainDefaultSize} min={LEFT_MAIN_MIN} miw={LEFT_MAIN_MIN}>
                    {leftMain}
                </Splitter.Pane>
                {rightTopOpen && (
                    <Splitter.Pane
                        defaultSize={rightTopDefaultSize}
                        min={(rightTopMin ?? RIGHT_TOP_MIN) as SplitterPaneSize}
                        max={rightTopMax as SplitterPaneSize | undefined}
                        miw={rightTopMin ?? RIGHT_TOP_MIN}
                        maw={rightTopMax}
                    >
                        <WorkspaceRightTop>{rightTop}</WorkspaceRightTop>
                    </Splitter.Pane>
                )}
            </Splitter>
        </Flex>
    );

    if (leftTopFullWidth) {
        return (
            <Flex direction="column" h="100%" w="100%">
                {leftTopOpen && <WorkspaceFullWidthTop height={leftTopHeight}>{leftTop}</WorkspaceFullWidthTop>}
                {sidebarAndPanes}
            </Flex>
        );
    }

    return sidebarAndPanes;
}
